import React, {FC} from 'react';
import styled from 'styled-components';

import {
  AccountAttributeUpdateAction,
  addCustomAttribute,
  deleteCustomAttribute,
  updateCustomAttribute,
} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {CrmSyncStatusesEnum} from '../../../../models/api/crmSyncModel';
import {CustomAttributeModel, CustomFieldModel} from '../../../../models/api/customFieldModel';
import {CustomAttributeValue} from '../../../common/customAttributes/customAttributeValue';
import {InlineEditingCustomAttribute} from '../../../common/inlineEditing/inlineEditingCustomAttribute';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';
import {AccountInformationAttribute} from './accountInformationAttribute';

/*
 * Props.
 */

interface CustomAttributeProps {
  accountId: number;
  customAttribute: CustomAttributeModel;
  isFieldCrmSyncing: boolean;
  crmSyncEditLink?: string;
  crmSyncStatus?: CrmSyncStatusesEnum;
  crmSyncSource?: string;
  shouldDisableActions?: boolean;
  updateAccountAttributeAsync: (action: AccountAttributeUpdateAction) => Promise<void>;
  updateUnsavedAccount: (action: AccountAttributeUpdateAction) => void;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

/*
 * Components.
 */

export const AccountCustomAttribute: FC<CustomAttributeProps> = (props) => {
  const {
    accountId,
    customAttribute,
    isFieldCrmSyncing,
    crmSyncEditLink,
    crmSyncSource,
    crmSyncStatus,
    shouldDisableActions,
    updateAccountAttributeAsync,
    updateUnsavedAccount,
  } = props;
  const isAddingNewCustomAttribute = customAttribute.value === '';
  const {onTrackEvent} = useAccountDetailsContext();

  return (
    <AccountInformationAttribute
      title={customAttribute.customField.name}
      currentAttributeValue={customAttribute.value}
      isFieldCrmSyncing={isFieldCrmSyncing}
      crmSyncEditLink={crmSyncEditLink}
      crmSyncStatus={crmSyncStatus}
      crmSyncSource={crmSyncSource}
      shouldDisableActions={shouldDisableActions}
      renderValue={(inlineEditingProps) => {
        if (inlineEditingProps.isEditing) {
          return (
            <InlineEditingCustomAttribute
              customAttribute={customAttribute}
              editedValue={inlineEditingProps.editedValue}
              onEdit={inlineEditingProps.onEdit}
              onEditSave={inlineEditingProps.onEditSave}
              onEditCancel={inlineEditingProps.onEditCancel}
              shouldStartInEditMode={inlineEditingProps.shouldStartInEditMode}
            />
          );
        }

        if (isAddingNewCustomAttribute) {
          return <StyledValueDiv>-</StyledValueDiv>;
        }

        return (
          <StyledValueDiv>
            <CustomAttributeValue customAttribute={customAttribute} />
          </StyledValueDiv>
        );
      }}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(
          isAddingNewCustomAttribute,
          customAttribute.value,
          customAttribute.customField,
          newValue,
        );
        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            isAddingNewCustomAttribute
              ? AccountDetailsEditActionsEnum.ADD
              : AccountDetailsEditActionsEnum.EDIT,
            AccountDetailsEditAttributesEnum.CUSTOM_FIELD,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteCustomAttribute(customAttribute.value, customAttribute.customField);

        // No need to hit the backend if the user deletes the custom attribute before it's saved
        if (isAddingNewCustomAttribute) {
          return updateUnsavedAccount(action);
        }

        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            AccountDetailsEditActionsEnum.DELETE,
            AccountDetailsEditAttributesEnum.CUSTOM_FIELD,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingNewCustomAttribute}
    />
  );
};

function computeOnSaveAction(
  isAddingNewCustomAttribute: boolean,
  currentValue: string,
  customField: CustomFieldModel,
  newValue: string,
) {
  if (isAddingNewCustomAttribute) {
    return addCustomAttribute(newValue, customField);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteCustomAttribute(currentValue, customField);
  }

  return updateCustomAttribute(currentValue, newValue, customField);
}
